// File: frontend/src/ThemeContext.js

import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

function getInitialTheme() {
    try {
        const saved = localStorage.getItem('theme');
        if (saved === 'light' || saved === 'dark') return saved;
    } catch (e) { /* ignore - e.g. private browsing */ }
    if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
}

export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(getInitialTheme);

    // Reflect the current theme on <html> so Tailwind's `dark:` variants apply.
    useEffect(() => {
        const root = document.documentElement;
        if (theme === 'dark') root.classList.add('dark');
        else root.classList.remove('dark');
    }, [theme]);

    // Keep following the OS theme live, but only until the user explicitly
    // picks one themselves - checked fresh each time rather than captured
    // once, so a manual toggle later in the session correctly stops this.
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e) => {
            let manual = null;
            try { manual = localStorage.getItem('theme'); } catch (err) { /* ignore */ }
            if (manual === null) setThemeState(e.matches ? 'dark' : 'light');
        };
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    const setTheme = (newTheme) => {
        setThemeState(newTheme);
        try { localStorage.setItem('theme', newTheme); } catch (e) { /* ignore */ }
    };

    const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

    return (
        <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export const useTheme = () => useContext(ThemeContext);
